import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingBag, MessageSquare, Image, Clock, LogOut, Trash2, Upload, Save,
  RefreshCw, Heart, Check, X as XIcon, Search, BarChart3, DollarSign,
  Users, Sparkles, Download, Mail, Eye, Send, ChevronDown, Plus
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import { adminApi } from "@/lib/adminApi";
import { PACKAGES as ORDER_PACKAGES, PURSE_TYPES, DESIGNERS, US_STATES, COUNTRIES } from "./order/constants";

const LOGO_URL = "/logo.png";

const STATUSES = ["pending", "confirmed", "scooped", "shipped", "delivered"];
const STATUS_COLORS = {
  pending: "bg-yellow-900/30 text-yellow-400 border-yellow-500/30",
  confirmed: "bg-blue-900/30 text-blue-400 border-blue-500/30",
  scooped: "bg-purple-900/30 text-purple-400 border-purple-500/30",
  shipped: "bg-orange-900/30 text-orange-400 border-orange-500/30",
  delivered: "bg-green-900/30 text-green-400 border-green-500/30",
};

const PIE_COLORS = ["#D4AF37", "#FF007F", "#4A90D9", "#4CAF50", "#E8923A"];
const CHART_GOLD = "#D4AF37";

const TABS = [
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "contacts", label: "Messages", icon: MessageSquare },
  { id: "gallery", label: "Gallery", icon: Image },
  { id: "reveals", label: "Reveals", icon: Heart },
  { id: "reviews", label: "Reviews", icon: Sparkles },
  { id: "countdown", label: "Countdown", icon: Clock },
];

const REVEAL_STATUS_BADGE = {
  pending: "bg-yellow-900/30 text-yellow-400",
  approved: "bg-green-900/30 text-green-400",
  rejected: "bg-red-900/30 text-red-400",
};

// ─── Chart Tooltip ───
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-[#D4AF37]/20 rounded-lg px-3 py-2 text-xs font-montserrat">
      <p className="text-white/60 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color || CHART_GOLD }}>
          {p.name}: {typeof p.value === "number" && p.name?.includes("evenue") ? `$${p.value.toLocaleString()}` : p.value}
        </p>
      ))}
    </div>
  );
}

// ─── Gallery Status Display ───
function GalleryStatusDisplay({ loading, isEmpty, children }) {
  if (loading) return <p className="text-white/40 font-montserrat text-sm">Loading gallery...</p>;
  if (isEmpty) return <p className="text-white/30 font-montserrat text-sm">No gallery images uploaded yet.</p>;
  return children;
}

// ─── Email Modal (reusable) ───
function EmailModal({ to, defaultSubject, defaultBody, onClose }) {
  const [subject, setSubject] = useState(defaultSubject || "");
  const [message, setMessage] = useState(defaultBody || "");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Subject and message are required"); return; }
    setSending(true);
    try {
      await adminApi.sendEmail({ to_email: to, subject, message });
      toast.success(`Email sent to ${to}`);
      onClose();
    } catch { toast.error("Failed to send email"); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-[#121212] border border-[#D4AF37]/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" data-testid="email-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-playfair text-white text-lg">Send Email</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-[#D4AF37]/80 text-xs tracking-[0.15em] uppercase font-montserrat mb-1">To</Label>
            <p className="font-montserrat text-white/60 text-sm bg-black/40 rounded-lg px-4 py-3 border border-[#D4AF37]/10">{to}</p>
          </div>
          <div>
            <Label className="text-[#D4AF37]/80 text-xs tracking-[0.15em] uppercase font-montserrat mb-1">Subject *</Label>
            <Input className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white font-montserrat h-11" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject-input" />
          </div>
          <div>
            <Label className="text-[#D4AF37]/80 text-xs tracking-[0.15em] uppercase font-montserrat mb-1">Message *</Label>
            <Textarea className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white font-montserrat min-h-[300px]" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Type your message..." data-testid="email-message-input" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={handleSend} disabled={sending} data-testid="email-send-btn" className="btn-gold flex-1 py-3 rounded-full text-xs font-montserrat flex items-center justify-center gap-2 disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> {sending ? "Sending..." : "Send Email"}
            </button>
            <button onClick={onClose} className="px-6 py-3 rounded-full text-xs font-montserrat text-white/40 border border-white/10 hover:border-white/20 transition-colors">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tracking Number Prompt Modal ───
function TrackingPromptModal({ onSubmit, onSkip, onClose }) {
  const [trackingNum, setTrackingNum] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-[#121212] border border-[#D4AF37]/30 rounded-2xl max-w-md w-full p-6" data-testid="tracking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-playfair text-white text-lg">Add Tracking Number</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors"><XIcon className="w-5 h-5" /></button>
        </div>
        <p className="font-montserrat text-white/50 text-xs mb-4">Enter the shipping tracking number. This will be included in the customer's email notification and shown on their tracking page.</p>
        <Input
          data-testid="tracking-number-input"
          className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white font-montserrat h-11 mb-4"
          placeholder="Enter tracking number"
          value={trackingNum}
          onChange={(e) => setTrackingNum(e.target.value)}
          autoFocus
        />
        <div className="flex gap-3">
          <button onClick={() => onSubmit(trackingNum)} disabled={!trackingNum.trim()} data-testid="tracking-submit-btn" className="btn-gold flex-1 py-3 rounded-full text-xs font-montserrat disabled:opacity-50">
            Save & Ship
          </button>
          <button onClick={onSkip} data-testid="tracking-skip-btn" className="px-6 py-3 rounded-full text-xs font-montserrat text-white/40 border border-white/10 hover:border-white/20 transition-colors">
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Order Detail Modal ───
function OrderDetailModal({ order, onClose, onEmail, onDelete }) {
  const [trackingInput, setTrackingInput] = useState(order?.tracking_number || "");
  const [savingTracking, setSavingTracking] = useState(false);

  const handleSaveTracking = async () => {
    if (!trackingInput.trim()) { toast.error("Enter a tracking number"); return; }
    setSavingTracking(true);
    try {
      await adminApi.updateOrderStatus(order.id, order.status, trackingInput.trim());
      toast.success("Tracking number saved");
      order.tracking_number = trackingInput.trim();
    } catch { toast.error("Failed to save tracking number"); }
    setSavingTracking(false);
  };

  if (!order) return null;
  const PRICES = { duchess: 225, countess: 325, princess: 425, marquess: 525, empress: 625, queen: 725 };
  const NAMES = { duchess: "The Duchess", countess: "The Countess", princess: "The Princess", marquess: "The Marquess", empress: "The Empress", queen: "The Queen" };
  const price = PRICES[order.package_id] || 0;
  const pkgName = NAMES[order.package_id] || order.package_id;
  const firstName = order.customer_name?.split(" ")[0] || "Valued Customer";

  const statusEmails = {
    pending: {
      label: "Order Received",
      subject: `Your Royal Order Has Arrived at Our Court - ${pkgName}`,
      body: `Dearest ${pkgName} ${firstName},\n\nIt is with the utmost delight and deepest gratitude that your exquisite order has arrived at our court.\n\nYour selection of the ${pkgName} Package is most fitting for one of your elegance and grace. With a touch of magic and guided by regal intuition, with carefully curated treasures worthy of your charm and delight.\n\nAs our establishment is newly welcomed into society, within ten days we shall present your Royal Mystery Scoop Ceremony, thoughtfully prepared for your viewing pleasure.\n\nIf you have not yet paid for your order, and you are truly enchanted by your treasures, we invite you to proceed with payment, as your delight is our highest priority.\n\nWe are truly honored by your patronage and eagerly await the pleasure of creating a most magical experience just for you.\n\n✨\n\nWith royal appreciation…\n\nVicki Ann's Luxury Mystery Scoop`,
    },
    confirmed: {
      label: "Order Confirmed",
      subject: `Your Royal Order is Confirmed - ${pkgName}`,
      body: `Dearest ${pkgName} ${firstName},\n\nIt is with the utmost delight and deepest gratitude that your exquisite order has been confirmed. We will record your royal package very soon and notify her highness when it is ready for your viewing pleasure.\n\nWe pray you will love your royal gift and will add your royal comments to the reveal.\n\nIf you have not yet paid for your order, we invite you to proceed with payment, as your delight is our highest priority.\n\n✨\n\nWith royal appreciation…\n\nVicki Ann's Luxury Mystery Scoop`,
    },
    scooped: {
      label: "Order Scooped",
      subject: `Your Royal Package Has Been Scooped! - ${pkgName}`,
      body: `Dearest ${pkgName} ${firstName},\n\nIt is with the utmost delight and deepest gratitude that your exquisite order has been scooped and recorded. We invite your royal highness to view your reveal on our social media pages, at your royal leisure.\n\nWe pray you will love your royal gift and will add your comments to the reveal.\n\nIf you have not yet paid for your order, we invite you to proceed with payment, as your delight is our highest priority.\n\n✨\n\nWith royal appreciation…\n\nVicki Ann's Luxury Mystery Scoop`,
    },
    shipped: {
      label: "Order Shipped",
      subject: `Your Royal Package Has Shipped! - ${pkgName}`,
      body: `Dearest ${pkgName} ${firstName},\n\nIt is with the utmost delight and deepest gratitude that your exquisite order has been shipped out and should be received to your court very soon.${trackingInput ? `\n\nPlease find your tracking number: ${trackingInput}` : ""}\n\nWe pray you will love your royal gift and will record and upload your reveal to any of our social sites or tag us in yours!\n\n✨\n\nWith royal appreciation…\n\nVicki Ann's Luxury Mystery Scoop`,
    },
    delivered: {
      label: "Order Delivered",
      subject: `Your Royal Package Has Arrived! - ${pkgName}`,
      body: `Dearest ${pkgName} ${firstName},\n\nYour luxurious mystery scoop has been delivered to your court! We hope every treasure inside brings you royal delight.\n\nPlease share your reveal on our social sites or tag us — we would be honored to see your joy!\n\n✨\n\nWith royal appreciation…\n\nVicki Ann's Luxury Mystery Scoop`,
    },
  };

  const Row = ({ label, value }) => value ? (
    <div className="flex justify-between py-2 border-b border-white/5">
      <span className="text-white/40 text-xs font-montserrat">{label}</span>
      <span className="text-white/80 text-xs font-montserrat text-right max-w-[60%]">{value}</span>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-[#121212] border border-[#D4AF37]/30 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" data-testid="order-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-playfair text-white text-lg">Order Details</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors"><XIcon className="w-5 h-5" /></button>
        </div>

        <div className="bg-black/40 rounded-xl border border-[#D4AF37]/15 p-4 mb-5">
          <p className="font-playfair text-[#D4AF37] text-xl font-bold">{NAMES[order.package_id] || order.package_id}</p>
          <p className="font-playfair text-white text-lg font-bold">${price}</p>
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-[10px] font-montserrat font-semibold border capitalize ${STATUS_COLORS[order.status] || STATUS_COLORS.pending}`}>{order.status}</span>
        </div>

        <div className="mb-4">
          <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase font-montserrat mb-2">Customer</p>
          <Row label="Name" value={order.customer_name} />
          <Row label="Email" value={order.email} />
          <Row label="Phone" value={order.phone} />
        </div>

        <div className="mb-4">
          <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase font-montserrat mb-2">Billing Address</p>
          <Row label="Street" value={order.billing_address || order.address} />
          <Row label="City" value={order.billing_city || order.city} />
          <Row label="State" value={order.billing_state || order.state} />
          <Row label="Zip" value={order.billing_zip || order.zip_code} />
          <Row label="Country" value={order.billing_country} />
        </div>

        <div className="mb-4">
          <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase font-montserrat mb-2">Shipping Address</p>
          {order.shipping_same_as_billing !== false ? (
            <p className="text-white/50 text-xs font-montserrat py-2">Same as billing</p>
          ) : (
            <>
              <Row label="Street" value={order.shipping_address} />
              <Row label="City" value={order.shipping_city} />
              <Row label="State" value={order.shipping_state} />
              <Row label="Zip" value={order.shipping_zip} />
              <Row label="Country" value={order.shipping_country} />
            </>
          )}
        </div>

        <div className="mb-4">
          <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase font-montserrat mb-2">Preferences</p>
          <Row label="Favorite Color" value={order.favorite_color} />
          <Row label="Purse Type" value={order.purse_type} />
          <Row label="Designer" value={order.favorite_designer} />
          <Row label="Jewelry Metal" value={order.jewelry_metal} />
          <Row label="Jewelry Style" value={order.jewelry_style} />
        </div>

        {order.notes && (
          <div className="mb-4">
            <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase font-montserrat mb-2">Special Instructions</p>
            <p className="text-white/70 text-xs font-montserrat leading-relaxed bg-black/30 rounded-lg p-3 border border-white/5 whitespace-pre-wrap">{order.notes}</p>
          </div>
        )}

        <p className="text-white/20 text-[10px] font-montserrat mb-5">Ordered: {new Date(order.created_at).toLocaleString()}</p>

        <div className="bg-[#E8923A]/10 border border-[#E8923A]/20 rounded-xl p-4 mb-5">
          <p className="text-[#E8923A]/60 text-[10px] tracking-[0.2em] uppercase font-montserrat mb-2">Tracking Number</p>
          <div className="flex gap-2">
            <Input
              data-testid="order-detail-tracking-input"
              className="bg-black/50 border-[#E8923A]/30 focus:border-[#E8923A] text-white font-montserrat h-10 flex-1 text-sm"
              placeholder="Enter tracking number"
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
            />
            <button onClick={handleSaveTracking} disabled={savingTracking} data-testid="order-detail-save-tracking-btn" className="px-4 py-2 rounded-lg bg-[#E8923A]/20 text-[#E8923A] text-xs font-montserrat font-semibold hover:bg-[#E8923A]/30 transition-colors disabled:opacity-50">
              {savingTracking ? "..." : "Save"}
            </button>
          </div>
        </div>

        {order.email && (
          <div className="space-y-2 mb-3">
            <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.2em] uppercase font-montserrat mb-1">Send Status Email</p>
            {Object.entries(statusEmails).map(([key, val]) => (
              <button
                key={key}
                onClick={() => onEmail({ to: order.email, subject: val.subject, body: val.body })}
                data-testid={`order-send-${key}-email`}
                className={`w-full py-2.5 rounded-lg text-xs font-montserrat flex items-center justify-between px-4 border transition-colors ${
                  key === order.status
                    ? "border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "border-white/10 bg-white/5 text-white/40 hover:border-[#D4AF37]/30 hover:text-[#D4AF37]/70"
                }`}
              >
                <span className="flex items-center gap-2"><Mail className="w-3 h-3" /> {val.label}</span>
                {key === order.status && <span className="text-[8px] bg-[#D4AF37]/20 px-2 py-0.5 rounded-full">Current</span>}
              </button>
            ))}
          </div>
        )}
        <button onClick={() => onDelete(order)} data-testid="order-detail-delete-btn" className="w-full py-3 rounded-full text-xs font-montserrat flex items-center justify-center gap-2 border border-red-500/20 text-red-400/60 hover:bg-red-900/20 hover:text-red-400 transition-colors">
          <Trash2 className="w-3.5 h-3.5" /> Delete Order
        </button>
      </div>
    </div>
  );
}

// ─── Analytics Tab ───
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    adminApi.getAnalytics()
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="text-white/40 font-montserrat text-sm">Loading analytics...</p>;
  if (!data) return <p className="text-white/30 font-montserrat text-sm">No data available.</p>;

  const kpis = [
    { label: "Total Revenue", value: `$${data.total_revenue.toLocaleString()}`, icon: DollarSign, accent: "#D4AF37" },
    { label: "Total Orders", value: data.total_orders, icon: ShoppingBag, accent: "#FF007F" },
    { label: "Page Views", value: data.visitor_stats?.total_views || 0, icon: Users, accent: "#4A90D9" },
    { label: "Unique Visitors", value: data.visitor_stats?.unique_visitors || 0, icon: Sparkles, accent: "#E8923A" },
  ];

  return (
    <div data-testid="admin-analytics-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="glass-card border border-[#D4AF37]/10 rounded-xl p-5" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-montserrat text-white/30 text-[10px] tracking-[0.2em] uppercase">{kpi.label}</span>
              <kpi.icon className="w-4 h-4" style={{ color: kpi.accent + "80" }} />
            </div>
            <p className="font-playfair text-white text-2xl lg:text-3xl font-bold">{kpi.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="glass-card border border-[#D4AF37]/10 rounded-xl p-5" data-testid="chart-order-trend">
          <h3 className="font-playfair text-white text-base mb-4">Order Trend</h3>
          {data.daily_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data.daily_trend}>
                <defs>
                  <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_GOLD} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={CHART_GOLD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: "#ffffff40", fontSize: 10 }} axisLine={{ stroke: "#ffffff10" }} tickLine={false} />
                <YAxis tick={{ fill: "#ffffff40", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="orders" name="Orders" stroke={CHART_GOLD} fill="url(#goldGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-white/20 text-xs font-montserrat py-16 text-center">No trend data yet</p>
          )}
        </div>

        <div className="glass-card border border-[#D4AF37]/10 rounded-xl p-5" data-testid="chart-status-breakdown">
          <h3 className="font-playfair text-white text-base mb-4">Order Status</h3>
          {data.status_breakdown.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={data.status_breakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={3} strokeWidth={0}>
                    {data.status_breakdown.map((entry) => (
                      <Cell key={entry.status} fill={PIE_COLORS[STATUSES.indexOf(entry.status) % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2.5 flex-1">
                {data.status_breakdown.map((s) => (
                  <div key={s.status} className="flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PIE_COLORS[STATUSES.indexOf(s.status) % PIE_COLORS.length] }} />
                    <span className="font-montserrat text-white/50 text-xs capitalize flex-1">{s.status}</span>
                    <span className="font-montserrat text-white text-xs font-semibold">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-white/20 text-xs font-montserrat py-16 text-center">No data yet</p>
          )}
        </div>
      </div>

      <div className="glass-card border border-[#D4AF37]/10 rounded-xl p-5" data-testid="chart-package-breakdown">
        <h3 className="font-playfair text-white text-base mb-4">Revenue by Package</h3>
        {data.package_breakdown.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.package_breakdown} barSize={32}>
              <XAxis dataKey="name" tick={{ fill: "#ffffff40", fontSize: 10 }} axisLine={{ stroke: "#ffffff10" }} tickLine={false} />
              <YAxis tick={{ fill: "#ffffff40", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="revenue" name="Revenue" fill={CHART_GOLD} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-white/20 text-xs font-montserrat py-16 text-center">No data yet</p>
        )}
      </div>

      {/* Visitor Analytics */}
      {data.visitor_stats && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="glass-card border border-[#D4AF37]/10 rounded-xl p-5" data-testid="chart-visitor-trend">
              <h3 className="font-playfair text-white text-base mb-4">Visitor Trend (Last 30 Days)</h3>
              {data.visitor_stats.daily_views?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={data.visitor_stats.daily_views}>
                    <defs>
                      <linearGradient id="blueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4A90D9" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#4A90D9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fill: "#ffffff40", fontSize: 10 }} axisLine={{ stroke: "#ffffff10" }} tickLine={false} />
                    <YAxis tick={{ fill: "#ffffff40", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="views" name="Page Views" stroke="#4A90D9" fill="url(#blueGrad)" strokeWidth={2} />
                    <Area type="monotone" dataKey="unique" name="Unique Visitors" stroke="#E8923A" fill="none" strokeWidth={2} strokeDasharray="5 5" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-white/20 text-xs font-montserrat py-16 text-center">No visitor data yet</p>
              )}
            </div>
            <div className="glass-card border border-[#D4AF37]/10 rounded-xl p-5" data-testid="page-breakdown">
              <h3 className="font-playfair text-white text-base mb-4">Top Pages</h3>
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-white/20 text-[10px] font-montserrat tracking-wider uppercase">Today</span>
                <span className="text-white/40 text-xs font-montserrat">{data.visitor_stats.today_views} views / {data.visitor_stats.today_unique} unique</span>
              </div>
              {data.visitor_stats.page_breakdown?.length > 0 ? (
                <div className="space-y-2">
                  {data.visitor_stats.page_breakdown.map((p) => {
                    const maxViews = data.visitor_stats.page_breakdown[0]?.views || 1;
                    const pct = Math.round((p.views / maxViews) * 100);
                    return (
                      <div key={p.page} className="relative">
                        <div className="absolute inset-0 rounded-lg bg-[#4A90D9]/10" style={{ width: `${pct}%` }} />
                        <div className="relative flex items-center justify-between px-3 py-2">
                          <span className="font-montserrat text-white/60 text-xs truncate max-w-[200px]">{p.page}</span>
                          <span className="font-montserrat text-white text-xs font-semibold">{p.views}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-white/20 text-xs font-montserrat py-16 text-center">No page data yet</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── New Order Modal (manual creation by admin) ───
const JEWELRY_METALS = ["Silver", "Gold", "Surprise Me!"];
const JEWELRY_STYLES = ["Bold and Gaudy", "Simple and Clean", "Surprise Me!"];

const NEW_ORDER_DEFAULTS = {
  customer_name: "",
  email: "",
  phone: "",
  billing_address: "",
  billing_city: "",
  billing_state: "",
  billing_zip: "",
  billing_country: "United States",
  shipping_same_as_billing: true,
  shipping_address: "",
  shipping_city: "",
  shipping_state: "",
  shipping_zip: "",
  shipping_country: "",
  package_id: "duchess",
  favorite_color: "",
  purse_type: "",
  favorite_designer: "",
  jewelry_metal: "",
  jewelry_style: "",
  notes: "",
};

function NewOrderModal({ onClose, onCreated }) {
  const [form, setForm] = useState(NEW_ORDER_DEFAULTS);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Name, email, and phone are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi.createOrder(form);
      toast.success(`Order created for ${data.customer_name}`);
      onCreated(data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      toast.error(`Failed to create order: ${detail}`);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" data-testid="new-order-modal">
      <form onSubmit={submit} className="glass-card border border-[#D4AF37]/30 rounded-2xl w-full max-w-2xl relative flex flex-col" style={{ maxHeight: "92vh" }}>
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white z-10 bg-black/50 rounded-full p-1.5" data-testid="new-order-close-btn">
          <XIcon className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-white/5">
          <h2 className="font-playfair text-[#D4AF37] text-lg sm:text-xl">+ Add Order Manually</h2>
          <p className="font-montserrat text-white/40 text-[11px] mt-0.5">Recreate a deleted order or add a phone/in-person order.</p>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input data-testid="new-order-name" placeholder="Customer name *" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="bg-black/50 border-[#D4AF37]/30 text-white h-10 text-sm" />
            <Input data-testid="new-order-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-black/50 border-[#D4AF37]/30 text-white h-10 text-sm" />
            <Input data-testid="new-order-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-black/50 border-[#D4AF37]/30 text-white h-10 text-sm" />
            <select data-testid="new-order-package" value={form.package_id} onChange={(e) => set("package_id", e.target.value)} className="bg-black/50 border border-[#D4AF37]/30 text-white h-10 px-3 rounded-md text-sm">
              {ORDER_PACKAGES.map((p) => (
                <option key={p.id} value={p.id} className="bg-[#1a1a1a]">{p.label} — ${p.price}</option>
              ))}
            </select>
          </div>

          <div>
            <p className="font-montserrat text-white/60 text-[10px] uppercase tracking-wider mb-2">Billing address</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Street address" value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} className="bg-black/50 border-[#D4AF37]/30 text-white h-10 text-sm sm:col-span-2" />
              <Input placeholder="City" value={form.billing_city} onChange={(e) => set("billing_city", e.target.value)} className="bg-black/50 border-[#D4AF37]/30 text-white h-10 text-sm" />
              <select value={form.billing_state} onChange={(e) => set("billing_state", e.target.value)} className="bg-black/50 border border-[#D4AF37]/30 text-white h-10 px-3 rounded-md text-sm">
                <option value="" className="bg-[#1a1a1a]">State</option>
                {US_STATES.map((s) => <option key={s} value={s} className="bg-[#1a1a1a]">{s}</option>)}
              </select>
              <Input placeholder="Zip" value={form.billing_zip} onChange={(e) => set("billing_zip", e.target.value)} className="bg-black/50 border-[#D4AF37]/30 text-white h-10 text-sm" />
              <select value={form.billing_country} onChange={(e) => set("billing_country", e.target.value)} className="bg-black/50 border border-[#D4AF37]/30 text-white h-10 px-3 rounded-md text-sm">
                {COUNTRIES.map((c) => <option key={c} value={c} className="bg-[#1a1a1a]">{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <p className="font-montserrat text-white/60 text-[10px] uppercase tracking-wider mb-2">Preferences</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Favorite color (e.g. Pink, Gold)" value={form.favorite_color} onChange={(e) => set("favorite_color", e.target.value)} className="bg-black/50 border-[#D4AF37]/30 text-white h-10 text-sm" />
              <select value={form.purse_type} onChange={(e) => set("purse_type", e.target.value)} className="bg-black/50 border border-[#D4AF37]/30 text-white h-10 px-3 rounded-md text-sm">
                <option value="" className="bg-[#1a1a1a]">Purse type</option>
                {PURSE_TYPES.map((p) => <option key={p} value={p} className="bg-[#1a1a1a]">{p}</option>)}
              </select>
              <select value={form.favorite_designer} onChange={(e) => set("favorite_designer", e.target.value)} className="bg-black/50 border border-[#D4AF37]/30 text-white h-10 px-3 rounded-md text-sm">
                <option value="" className="bg-[#1a1a1a]">Favorite designer</option>
                {DESIGNERS.map((d) => <option key={d} value={d} className="bg-[#1a1a1a]">{d}</option>)}
              </select>
              <select value={form.jewelry_metal} onChange={(e) => set("jewelry_metal", e.target.value)} className="bg-black/50 border border-[#D4AF37]/30 text-white h-10 px-3 rounded-md text-sm">
                <option value="" className="bg-[#1a1a1a]">Jewelry metal</option>
                {JEWELRY_METALS.map((m) => <option key={m} value={m} className="bg-[#1a1a1a]">{m}</option>)}
              </select>
              <select value={form.jewelry_style} onChange={(e) => set("jewelry_style", e.target.value)} className="bg-black/50 border border-[#D4AF37]/30 text-white h-10 px-3 rounded-md text-sm sm:col-span-2">
                <option value="" className="bg-[#1a1a1a]">Jewelry style</option>
                {JEWELRY_STYLES.map((s) => <option key={s} value={s} className="bg-[#1a1a1a]">{s}</option>)}
              </select>
            </div>
          </div>

          <Textarea placeholder="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-black/50 border-[#D4AF37]/30 text-white text-sm" rows={2} />
        </div>

        {/* Sticky footer with submit */}
        <div className="px-5 py-3 border-t border-white/5 flex justify-end gap-2 bg-black/30 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-full text-white/50 text-sm font-montserrat hover:text-white">Cancel</button>
          <button type="submit" disabled={saving} data-testid="new-order-submit-btn" className="btn-gold px-6 py-2 rounded-full text-sm font-montserrat disabled:opacity-50">
            {saving ? "Saving..." : "Create Order"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Trash Modal — view & restore deleted orders ───
function TrashModal({ onClose, onRestored }) {
  const [trashed, setTrashed] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getTrashedOrders();
      setTrashed(data);
    } catch { toast.error("Failed to load trash"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const restore = async (order) => {
    try {
      await adminApi.restoreOrder(order.id);
      setTrashed((prev) => prev.filter((o) => o.id !== order.id));
      toast.success(`Restored ${order.customer_name}'s order`);
      onRestored(order);
    } catch { toast.error("Restore failed"); }
  };

  const purge = async (order) => {
    if (!window.confirm(`Permanently delete order from ${order.customer_name}? This CANNOT be undone.`)) return;
    try {
      await adminApi.purgeOrder(order.id);
      setTrashed((prev) => prev.filter((o) => o.id !== order.id));
      toast.success("Order permanently deleted");
    } catch { toast.error("Permanent delete failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="trash-modal">
      <div className="glass-card border border-[#D4AF37]/30 rounded-2xl p-6 w-full max-w-3xl my-8 relative max-h-[85vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-white/30 hover:text-white" data-testid="trash-close-btn">
          <XIcon className="w-5 h-5" />
        </button>
        <h2 className="font-playfair text-[#D4AF37] text-xl mb-1 flex items-center gap-2">
          <Trash2 className="w-5 h-5" /> Deleted Orders ({trashed.length})
        </h2>
        <p className="font-montserrat text-white/40 text-xs mb-5">Restore to put back in your active orders, or permanently delete to remove forever.</p>

        {loading ? (
          <p className="text-white/40 font-montserrat text-sm">Loading...</p>
        ) : trashed.length === 0 ? (
          <p className="text-white/30 font-montserrat text-sm">Trash is empty.</p>
        ) : (
          <div className="space-y-3">
            {trashed.map((o) => (
              <div key={o.id} className="border border-white/10 rounded-xl p-4" data-testid={`trash-order-${o.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-playfair text-white font-semibold truncate">{o.customer_name}</p>
                    <p className="font-montserrat text-white/40 text-xs truncate">{o.email} &middot; {o.phone}</p>
                    <p className="font-montserrat text-white/30 text-[10px] mt-1">
                      Package: <span className="text-[#D4AF37] capitalize">{o.package_id}</span> &middot; Deleted: {o.deleted_at ? new Date(o.deleted_at).toLocaleString() : "?"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => restore(o)}
                      data-testid={`restore-order-${o.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-900/20 text-green-400 text-xs font-montserrat hover:bg-green-900/40 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" /> Restore
                    </button>
                    <button
                      onClick={() => purge(o)}
                      data-testid={`purge-order-${o.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-900/20 text-red-400 text-xs font-montserrat hover:bg-red-900/40 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" /> Forever
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Orders Tab ───
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [trackingPrompt, setTrackingPrompt] = useState(null);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [showTrash, setShowTrash] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getOrders();
      setOrders(data);
    } catch { toast.error("Failed to load orders"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = useCallback(async (orderId, newStatus, trackingNumber) => {
    if (newStatus === "shipped" && !trackingNumber) {
      setTrackingPrompt({ orderId, status: newStatus });
      return;
    }
    try {
      await adminApi.updateOrderStatus(orderId, newStatus, trackingNumber);
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: newStatus, tracking_number: trackingNumber || o.tracking_number } : o));
      toast.success(`Order updated to ${newStatus}`);
    } catch { toast.error("Failed to update status"); }
  }, []);

  const exportCSV = useCallback(async () => {
    try {
      const { data } = await adminApi.exportOrders();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "orders_export.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Orders exported");
    } catch { toast.error("Export failed"); }
  }, []);

  const deleteOrder = useCallback(async (orderId, name) => {
    if (!window.confirm(`Move order from ${name} to trash? You can restore it later.`)) return;
    try {
      await adminApi.deleteOrder(orderId);
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      setSelectedOrder(null);
      toast.success("Moved to trash");
    } catch { toast.error("Failed to move to trash"); }
  }, []);

  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    const matchesSearch = !q || o.customer_name?.toLowerCase().includes(q) || o.email?.toLowerCase().includes(q) || o.phone?.includes(q) || o.package_id?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === "all" || o.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) return <p className="text-white/40 font-montserrat text-sm">Loading orders...</p>;

  return (
    <div data-testid="admin-orders-tab">
      {showTrash && (
        <TrashModal
          onClose={() => setShowTrash(false)}
          onRestored={(order) => setOrders((prev) => [{ ...order, deleted_at: undefined }, ...prev])}
        />
      )}
      {showNewOrder && (
        <NewOrderModal
          onClose={() => setShowNewOrder(false)}
          onCreated={(newOrder) => setOrders((prev) => [newOrder, ...prev])}
        />
      )}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onEmail={(data) => { setSelectedOrder(null); setEmailTarget({ to: data.to, subject: data.subject, body: data.body }); }}
          onDelete={(o) => deleteOrder(o.id, o.customer_name)}
        />
      )}
      {emailTarget && (
        <EmailModal to={emailTarget.to} defaultSubject={emailTarget.subject} defaultBody={emailTarget.body || ""} onClose={() => setEmailTarget(null)} />
      )}
      {trackingPrompt && (
        <TrackingPromptModal
          onSubmit={(trackingNum) => { updateStatus(trackingPrompt.orderId, trackingPrompt.status, trackingNum); setTrackingPrompt(null); }}
          onSkip={() => { updateStatus(trackingPrompt.orderId, trackingPrompt.status, ""); setTrackingPrompt(null); }}
          onClose={() => setTrackingPrompt(null)}
        />
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h2 className="font-playfair text-white text-xl">Orders ({filtered.length}{filtered.length !== orders.length ? ` / ${orders.length}` : ""})</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
            <input
              data-testid="orders-search-input"
              className="bg-black/50 border border-[#D4AF37]/20 rounded-full pl-9 pr-4 py-2 text-xs text-white font-montserrat placeholder:text-white/25 focus:border-[#D4AF37]/50 focus:outline-none w-52"
              placeholder="Search name, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="orders-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-black/50 border border-[#D4AF37]/20 rounded-full px-4 py-2 text-xs text-white font-montserrat capitalize focus:border-[#D4AF37]/50 focus:outline-none appearance-none cursor-pointer"
          >
            <option value="all" className="bg-[#1a1a1a]">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s} className="bg-[#1a1a1a]">{s}</option>)}
          </select>
          <button onClick={() => setShowTrash(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-white/50 hover:text-white hover:border-white/30 transition-colors text-xs font-montserrat" data-testid="view-trash-btn">
            <Trash2 className="w-3.5 h-3.5" /> Trash
          </button>
          <button onClick={() => setShowNewOrder(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-colors text-xs font-montserrat" data-testid="new-order-btn">
            <Plus className="w-3.5 h-3.5" /> New Order
          </button>
          <button onClick={fetchOrders} className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors" data-testid="refresh-orders-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={exportCSV} className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#D4AF37]/20 text-[#D4AF37]/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors text-xs font-montserrat" data-testid="export-orders-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="text-white/30 font-montserrat text-sm">{orders.length === 0 ? "No orders yet." : "No orders match your filters."}</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => (
            <div key={order.id} className="glass-card border border-[#D4AF37]/10 rounded-xl p-5" data-testid={`order-card-${order.id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                <div>
                  <p className="font-playfair text-white font-semibold">{order.customer_name}</p>
                  <p className="font-montserrat text-white/40 text-xs">{order.email} &middot; {order.phone}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-montserrat text-[#D4AF37] text-sm font-semibold capitalize">{order.package_id}</span>
                  <select
                    value={order.status}
                    onChange={(e) => updateStatus(order.id, e.target.value)}
                    data-testid={`order-status-select-${order.id}`}
                    className={`px-3 py-1 rounded-full text-xs font-montserrat font-semibold border cursor-pointer appearance-none bg-transparent outline-none capitalize ${STATUS_COLORS[order.status] || STATUS_COLORS.pending}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s} className="bg-[#1a1a1a] text-white">{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-montserrat">
                <div><span className="text-white/30">Color:</span> <span className="text-white/70">{order.favorite_color}</span></div>
                <div><span className="text-white/30">Purse:</span> <span className="text-white/70">{order.purse_type}</span></div>
                <div><span className="text-white/30">Designer:</span> <span className="text-white/70">{order.favorite_designer}</span></div>
                <div><span className="text-white/30">Jewelry:</span> <span className="text-white/70">{order.jewelry_metal || "N/A"} / {order.jewelry_style || "N/A"}</span></div>
              </div>
              {order.notes && <p className="text-white/30 text-xs font-montserrat mt-2 truncate">Notes: {order.notes}</p>}
              {order.tracking_number && (
                <div className="text-xs font-montserrat mt-2">
                  <span className="text-white/30">Tracking:</span> <span className="text-[#E8923A] font-semibold">{order.tracking_number}</span>
                </div>
              )}
              <div className="flex items-center justify-between mt-3">
                <p className="text-white/20 text-[10px] font-montserrat">{new Date(order.created_at).toLocaleString()}</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedOrder(order)} data-testid={`order-view-${order.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 text-white/40 text-[10px] font-montserrat hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors">
                    <Eye className="w-3 h-3" /> View Details
                  </button>
                  {order.email && (
                    <button onClick={() => setEmailTarget({ to: order.email, subject: `Regarding your ${order.package_id} order — Vicki Ann's Luxurious Gifts` })} data-testid={`order-email-${order.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 text-white/40 text-[10px] font-montserrat hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors">
                      <Mail className="w-3 h-3" /> Email
                    </button>
                  )}
                  <button onClick={() => deleteOrder(order.id, order.customer_name)} data-testid={`order-delete-${order.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 text-white/40 text-[10px] font-montserrat hover:bg-red-900/20 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Contacts Tab ───
function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailTarget, setEmailTarget] = useState(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getContacts();
      setContacts(data);
    } catch { toast.error("Failed to load contacts"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  if (loading) return <p className="text-white/40 font-montserrat text-sm">Loading messages...</p>;

  return (
    <div data-testid="admin-contacts-tab">
      {emailTarget && (
        <EmailModal to={emailTarget.to} defaultSubject={emailTarget.subject} defaultBody={emailTarget.body || ""} onClose={() => setEmailTarget(null)} />
      )}
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-white text-xl">Messages ({contacts.length})</h2>
        <button onClick={fetchContacts} className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors" data-testid="refresh-contacts-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {contacts.length === 0 ? (
        <p className="text-white/30 font-montserrat text-sm">No messages yet.</p>
      ) : (
        <div className="space-y-3">
          {contacts.map((c) => (
            <div key={c.id} className="glass-card border border-[#D4AF37]/10 rounded-xl p-5" data-testid={`contact-card-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-playfair text-white font-semibold">{c.name}</p>
                <p className="text-white/20 text-[10px] font-montserrat">{new Date(c.created_at).toLocaleString()}</p>
              </div>
              <p className="font-montserrat text-white/40 text-xs mb-2">{c.email}</p>
              <p className="font-montserrat text-white/60 text-sm whitespace-pre-wrap mb-3">{c.message}</p>
              <button
                onClick={() => setEmailTarget({ to: c.email, subject: `Re: Your message — Vicki Ann's Luxurious Gifts` })}
                data-testid={`contact-reply-${c.id}`}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/5 text-white/40 text-[10px] font-montserrat hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors"
              >
                <Mail className="w-3 h-3" /> Reply
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Gallery Tab ───
function GalleryTab() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getGallery();
      setImages(data);
    } catch { toast.error("Failed to load gallery"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const handleUpload = useCallback(async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        await adminApi.uploadGallery(form);
        toast.success(`Uploaded ${file.name}`);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    fetchImages();
  }, [fetchImages]);

  const handleDelete = useCallback(async (id) => {
    try {
      await adminApi.deleteGallery(id);
      toast.success("Image deleted");
      fetchImages();
    } catch { toast.error("Failed to delete image"); }
  }, [fetchImages]);

  return (
    <div data-testid="admin-gallery-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-white text-xl">Gallery ({images.length})</h2>
        <label className="btn-gold px-5 py-2 rounded-full text-xs font-montserrat cursor-pointer flex items-center gap-2" data-testid="gallery-upload-btn">
          <Upload className="w-3 h-3" />
          {uploading ? "Uploading..." : "Upload Images"}
          <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" disabled={uploading} />
        </label>
      </div>
      <GalleryStatusDisplay loading={loading} isEmpty={images.length === 0}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img) => (
            <div key={img.id} className="relative group rounded-xl overflow-hidden border border-[#D4AF37]/10" data-testid={`gallery-image-${img.id}`}>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                alt={img.original_name}
                className="w-full h-40 object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => handleDelete(img.id)}
                  className="w-10 h-10 rounded-full bg-red-900/80 flex items-center justify-center text-red-300 hover:bg-red-800 transition-colors"
                  data-testid={`gallery-delete-${img.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="absolute bottom-0 left-0 right-0 bg-black/80 px-2 py-1 text-[10px] font-montserrat text-white/40 truncate">
                {img.original_name}
              </p>
            </div>
          ))}
        </div>
      </GalleryStatusDisplay>
    </div>
  );
}

// ─── Reveals Tab ───
function RevealsTab() {
  const [reveals, setReveals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReveals = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getReveals();
      setReveals(data);
    } catch { toast.error("Failed to load reveals"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReveals(); }, [fetchReveals]);

  const handleAction = useCallback(async (id, action) => {
    try {
      if (action === "delete") await adminApi.deleteReveal(id);
      else if (action === "approve") await adminApi.approveReveal(id);
      else if (action === "reject") await adminApi.rejectReveal(id);
      toast.success(`Reveal ${action === "delete" ? "deleted" : action + "d"}`);
      fetchReveals();
    } catch { toast.error(`Failed to ${action} reveal`); }
  }, [fetchReveals]);

  if (loading) return <p className="text-white/40 font-montserrat text-sm">Loading reveals...</p>;

  return (
    <div data-testid="admin-reveals-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-white text-xl">Customer Reveals ({reveals.length})</h2>
        <button onClick={fetchReveals} className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors" data-testid="refresh-reveals-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {reveals.length === 0 ? (
        <p className="text-white/30 font-montserrat text-sm">No reveals submitted yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reveals.map((r) => (
            <div key={r.id} className="glass-card border border-[#D4AF37]/10 rounded-xl overflow-hidden" data-testid={`admin-reveal-${r.id}`}>
              {r.type === "video" || /\.(mp4|mov|avi|webm)$/i.test(r.url || "") ? (
                <video
                  src={`${process.env.REACT_APP_BACKEND_URL}${r.url}`}
                  className="w-full h-48 object-cover bg-black"
                  controls
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${r.url}`}
                  alt={r.caption || "Reveal"}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-playfair text-white font-semibold text-sm">{r.name}</p>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-montserrat font-semibold capitalize ${REVEAL_STATUS_BADGE[r.status] || ""}`}>
                    {r.status}
                  </span>
                </div>
                {r.caption && <p className="font-montserrat text-white/40 text-xs mb-3">{r.caption}</p>}
                <p className="text-white/20 text-[10px] font-montserrat mb-3">{new Date(r.submitted_at).toLocaleString()}</p>
                <div className="flex gap-2">
                  {r.status !== "approved" && (
                    <button onClick={() => handleAction(r.id, "approve")} data-testid={`reveal-approve-${r.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-900/20 text-green-400 text-[10px] font-montserrat hover:bg-green-900/40 transition-colors">
                      <Check className="w-3 h-3" /> Approve
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button onClick={() => handleAction(r.id, "reject")} data-testid={`reveal-reject-${r.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-900/20 text-red-400 text-[10px] font-montserrat hover:bg-red-900/40 transition-colors">
                      <XIcon className="w-3 h-3" /> Reject
                    </button>
                  )}
                  <button onClick={() => handleAction(r.id, "delete")} data-testid={`reveal-delete-${r.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 text-white/30 text-[10px] font-montserrat hover:bg-white/10 transition-colors">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Reviews Tab ───
function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending | approved | rejected | all

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getReviews();
      setReviews(data);
    } catch { toast.error("Failed to load reviews"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const approve = async (r) => {
    try {
      await adminApi.approveReview(r.id);
      setReviews((prev) => prev.map((x) => x.id === r.id ? { ...x, status: "approved" } : x));
      toast.success(`Approved review from ${r.name} — now live on the site`);
    } catch { toast.error("Approve failed"); }
  };

  const reject = async (r) => {
    try {
      await adminApi.rejectReview(r.id);
      setReviews((prev) => prev.map((x) => x.id === r.id ? { ...x, status: "rejected" } : x));
      toast.success(`Review from ${r.name} hidden from the site`);
    } catch { toast.error("Reject failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Permanently delete review from ${r.name}? This cannot be undone.`)) return;
    try {
      await adminApi.deleteReview(r.id);
      setReviews((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Review deleted");
    } catch { toast.error("Delete failed"); }
  };

  // Default missing status to "pending" for legacy rows
  const normalized = reviews.map((r) => ({ ...r, status: r.status || "pending" }));
  const filtered = filter === "all" ? normalized : normalized.filter((r) => r.status === filter);
  const counts = {
    pending: normalized.filter((r) => r.status === "pending").length,
    approved: normalized.filter((r) => r.status === "approved").length,
    rejected: normalized.filter((r) => r.status === "rejected").length,
    all: normalized.length,
  };

  const statusBadge = (s) => {
    const cls = s === "approved"
      ? "bg-green-900/30 text-green-400 border-green-500/30"
      : s === "rejected"
        ? "bg-red-900/30 text-red-400 border-red-500/30"
        : "bg-yellow-900/30 text-yellow-400 border-yellow-500/30";
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-montserrat tracking-wider border ${cls}`}>{s.toUpperCase()}</span>;
  };

  return (
    <div data-testid="admin-reviews-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-playfair text-[#D4AF37] text-xl">Customer Reviews</h2>
          <p className="font-montserrat text-white/40 text-xs">Review submissions before they appear on your public site.</p>
        </div>
        <button onClick={fetchAll} className="text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors" data-testid="refresh-reviews-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`review-filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-xs font-montserrat tracking-wider transition-colors ${
              filter === f
                ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30"
                : "text-white/40 border border-white/10 hover:text-white/70"
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-white/40 font-montserrat text-sm">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-white/30 font-montserrat text-sm">No {filter === "all" ? "" : filter} reviews.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((r) => (
            <div key={r.id} className="glass-card border border-[#D4AF37]/10 rounded-xl p-4" data-testid={`admin-review-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-playfair text-white font-semibold">{r.name}</p>
                    <span className="text-[#D4AF37] text-xs">{"★".repeat(r.rating || 5)}</span>
                    {statusBadge(r.status)}
                  </div>
                  <p className="font-montserrat text-white/30 text-[10px] mt-1">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                  </p>
                </div>
              </div>
              <p className="font-montserrat text-white/60 text-sm mb-3">{r.text}</p>
              {r.photo_url && (
                <img src={r.photo_url.startsWith("http") ? r.photo_url : `${process.env.REACT_APP_BACKEND_URL}${r.photo_url}`} alt="" className="w-full h-32 object-cover rounded-lg mb-3 border border-white/5" />
              )}

              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5">
                {r.status !== "approved" && (
                  <button
                    onClick={() => approve(r)}
                    data-testid={`approve-review-${r.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-900/20 text-green-400 text-xs font-montserrat hover:bg-green-900/40 transition-colors"
                  >
                    <Check className="w-3 h-3" /> Approve
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button
                    onClick={() => reject(r)}
                    data-testid={`reject-review-${r.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-yellow-900/20 text-yellow-400 text-xs font-montserrat hover:bg-yellow-900/40 transition-colors"
                  >
                    <XIcon className="w-3 h-3" /> Reject
                  </button>
                )}
                <button
                  onClick={() => remove(r)}
                  data-testid={`delete-review-${r.id}`}
                  className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-900/20 text-red-400 text-xs font-montserrat hover:bg-red-900/40 transition-colors"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Countdown Tab ───
function CountdownTab() {
  const [config, setConfig] = useState({ date: "", title: "", platform: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi.getCountdown()
      .then(({ data }) => { if (!cancelled) setConfig({ date: data.date || "", title: data.title || "", platform: data.platform || "" }); })
      .catch(() => toast.error("Failed to load countdown config"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await adminApi.updateCountdown(config);
      toast.success("Countdown updated!");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  }, [config]);

  if (loading) return <p className="text-white/40 font-montserrat text-sm">Loading...</p>;

  const labelClass = "text-[#D4AF37]/80 text-xs tracking-[0.2em] uppercase font-montserrat mb-2";
  const inputClass = "bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white h-12 font-montserrat placeholder:text-white/30";

  return (
    <div data-testid="admin-countdown-tab" className="max-w-lg">
      <h2 className="font-playfair text-white text-xl mb-6">Countdown Timer</h2>
      <div className="space-y-5">
        <div>
          <Label className={labelClass}>Event Date & Time</Label>
          <Input
            data-testid="countdown-date-input"
            type="datetime-local"
            className={inputClass}
            value={config.date ? config.date.slice(0, 16) : ""}
            onChange={(e) => setConfig((prev) => ({ ...prev, date: e.target.value + ":00" }))}
          />
        </div>
        <div>
          <Label className={labelClass}>Event Title</Label>
          <Input
            data-testid="countdown-title-input"
            className={inputClass}
            placeholder="e.g., Next Live Scoop"
            value={config.title}
            onChange={(e) => setConfig((prev) => ({ ...prev, title: e.target.value }))}
          />
        </div>
        <div>
          <Label className={labelClass}>Platform</Label>
          <Input
            data-testid="countdown-platform-input"
            className={inputClass}
            placeholder="e.g., TikTok & Facebook Live"
            value={config.platform}
            onChange={(e) => setConfig((prev) => ({ ...prev, platform: e.target.value }))}
          />
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="countdown-save-btn"
          className="btn-gold px-8 py-3 rounded-full text-sm font-montserrat flex items-center gap-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───
export default function AdminDashboard() {
  const [tab, setTab] = useState("analytics");
  const navigate = useNavigate();

  useEffect(() => {
    adminApi.me().catch(() => navigate("/admin"));
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    await adminApi.logout().catch(() => {});
    navigate("/admin");
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-dashboard">
      <header className="border-b border-[#D4AF37]/15 bg-black/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Vicki Ann's" className="h-10" />
            <span className="font-montserrat text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase hidden sm:block">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="font-montserrat text-white/30 text-xs hover:text-[#D4AF37] transition-colors">View Site</a>
            <button onClick={handleLogout} className="flex items-center gap-2 text-white/40 hover:text-red-400 transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
              <span className="font-montserrat text-xs hidden sm:block">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8 pb-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-montserrat text-xs tracking-wider transition-all whitespace-nowrap ${
                tab === t.id
                  ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30"
                  : "text-white/40 border border-transparent hover:text-white/60"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "analytics" && <AnalyticsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "contacts" && <ContactsTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "reveals" && <RevealsTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "countdown" && <CountdownTab />}
      </div>
    </div>
  );
}
